import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useAuthStore } from '@/store/authStore';
import { useUsers } from '@/hooks/useUsers';
import crmStandupService, {
  type StandupAttendance,
  type StandupTeamRow,
} from '@/services/crmStandup.service';
import { crmPersonName } from '@/components/crm/activities/CrmActivityTimeline';
import { formatDate } from '@/utils/format/date';

const { Title, Paragraph, Text } = Typography;

const StandupTeamPage: React.FC = () => {
  const { message, modal } = App.useApp();
  const qc = useQueryClient();
  const { user } = useAuthStore();
  const canManage = Boolean(
    user?.is_super_admin || user?.permissions?.includes('crm.standup.manage')
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get('date') || dayjs().format('YYYY-MM-DD');
  const [date, setDate] = useState<Dayjs>(dayjs(initial));
  const dateStr = date.format('YYYY-MM-DD');
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decisionForm] = Form.useForm();

  const { data: users = [] } = useUsers({ is_active: true });
  const userMap = useMemo(() => new Map(users.map((u: any) => [u.id, u])), [users]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['crm-standup-team', dateStr],
    queryFn: () => crmStandupService.teamOverview(dateStr),
    enabled: canManage,
  });

  const { data: spiTeam } = useQuery({
    queryKey: ['spi-team', dateStr],
    queryFn: () => crmStandupService.spiTeam(),
    enabled: canManage,
    retry: false,
  });

  const attendanceMut = useMutation({
    mutationFn: (updates: Array<{ user_id: string; attendance: StandupAttendance }>) =>
      crmStandupService.setAttendance(data!.meeting.id, updates),
    onSuccess: () => {
      message.success('Attendance updated');
      qc.invalidateQueries({ queryKey: ['crm-standup-team', dateStr] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Attendance update failed'),
  });

  const decisionMut = useMutation({
    mutationFn: (body: {
      decision_text: string;
      owner_user_id?: string;
      due_date?: string;
      create_follow_up?: boolean;
    }) => crmStandupService.addDecision(data!.meeting.id, body),
    onSuccess: () => {
      message.success('Decision recorded (follow-up linked when due date set)');
      setDecisionOpen(false);
      decisionForm.resetFields();
      refetch();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Could not add decision'),
  });

  const finalizeMut = useMutation({
    mutationFn: () => crmStandupService.finalize(data!.meeting.id),
    onSuccess: () => {
      message.success('Stand-up finalized — snapshot locked');
      qc.invalidateQueries({ queryKey: ['crm-standup-team', dateStr] });
      qc.invalidateQueries({ queryKey: ['crm-standup-meetings'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || 'Finalize failed'),
  });

  if (!canManage) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Manager access required"
        description={
          <span>
            You need <Text code>crm.standup.manage</Text>.{' '}
            <Link to="/growth/standup">Go to my stand-up brief</Link>
          </span>
        }
      />
    );
  }

  const finalized = data?.meeting?.status === 'Finalized';
  const columns: ColumnsType<StandupTeamRow> = [
    {
      title: 'Salesperson',
      dataIndex: 'user_id',
      render: (id: string) => crmPersonName(userMap.get(id)) || id.slice(0, 8),
    },
    {
      title: 'Attendance',
      dataIndex: 'attendance',
      width: 160,
      render: (v: StandupAttendance, row) => (
        <Select
          size="small"
          value={v}
          disabled={finalized || attendanceMut.isPending}
          style={{ width: 130 }}
          options={[
            { value: 'Present', label: 'Present' },
            { value: 'Absent', label: 'Absent' },
            { value: 'Delegated', label: 'Delegated' },
          ]}
          onChange={(attendance) =>
            attendanceMut.mutate([{ user_id: row.user_id, attendance }])
          }
        />
      ),
    },
    {
      title: 'Submitted',
      dataIndex: 'submitted_at',
      render: (v?: string | null) => (v ? <Tag color="blue">{formatDate(v)}</Tag> : <Tag>Pending</Tag>),
    },
    {
      title: 'Overdue',
      render: (_, r) => (
        <Tag color={r.continuity.counts.overdue ? 'red' : undefined}>{r.continuity.counts.overdue}</Tag>
      ),
    },
    {
      title: 'Postponed',
      render: (_, r) => (
        <Tag color={r.continuity.counts.postponed ? 'orange' : undefined}>
          {r.continuity.counts.postponed}
        </Tag>
      ),
    },
    {
      title: 'Done',
      render: (_, r) => r.continuity.counts.completed,
    },
    {
      title: '',
      render: (_, r) => (
        <Link to={`/growth/standup?date=${dateStr}&focus=${r.user_id}`}>Brief</Link>
      ),
    },
  ];

  const spiRows = Array.isArray(spiTeam)
    ? spiTeam
    : Array.isArray((spiTeam as any)?.members)
      ? (spiTeam as any).members
      : [];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Row justify="space-between" align="middle" gutter={[16, 16]}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Stand-Up — Team console
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Attendance, commitment continuity, decisions, and finalize.
          </Paragraph>
        </Col>
        <Col>
          <Space wrap>
            <DatePicker
              value={date}
              allowClear={false}
              onChange={(d) => {
                if (!d) return;
                setDate(d);
                setSearchParams({ date: d.format('YYYY-MM-DD') });
              }}
            />
            <Button>
              <Link to={`/growth/standup?date=${dateStr}`}>My brief</Link>
            </Button>
            <Button>
              <Link to="/growth/standup/history">History</Link>
            </Button>
            <Button disabled={finalized} onClick={() => setDecisionOpen(true)}>
              Add decision
            </Button>
            <Button
              type="primary"
              disabled={finalized || !data?.meeting}
              loading={finalizeMut.isPending}
              onClick={() =>
                modal.confirm({
                  title: 'Finalize stand-up?',
                  content:
                    'Creates an immutable snapshot and publishes the deterministic summary. Live CRM continues separately.',
                  onOk: () => finalizeMut.mutateAsync(),
                })
              }
            >
              Finalize
            </Button>
          </Space>
        </Col>
      </Row>

      {error && (
        <Alert type="error" showIcon message={(error as any)?.response?.data?.message || 'Load failed'} />
      )}

      {data?.meeting && (
        <Space wrap>
          <Tag color={finalized ? 'green' : 'processing'}>{data.meeting.status}</Tag>
          <Text type="secondary">Meeting {formatDate(data.meeting.meeting_date)}</Text>
          {finalized && <Text type="secondary">Locked</Text>}
        </Space>
      )}

      {!!spiRows.length && (
        <Card size="small" title="Team SPI strip">
          <Space wrap>
            {spiRows.slice(0, 12).map((m: any) => (
              <Tag key={m.user_id || m.id}>
                {crmPersonName(userMap.get(m.user_id)) || m.display_name || m.user_id?.slice?.(0, 8)}{' '}
                {m.pace || m.on_track_status || ''}
              </Tag>
            ))}
          </Space>
        </Card>
      )}

      <Card>
        <Table
          rowKey="participant_id"
          loading={isLoading}
          columns={columns}
          dataSource={data?.participants || []}
          pagination={false}
          size="middle"
        />
      </Card>

      <Modal
        title="Management decision"
        open={decisionOpen}
        onCancel={() => setDecisionOpen(false)}
        onOk={() => decisionForm.submit()}
        confirmLoading={decisionMut.isPending}
        destroyOnClose
      >
        <Form
          form={decisionForm}
          layout="vertical"
          onFinish={(v) =>
            decisionMut.mutate({
              decision_text: v.decision_text,
              owner_user_id: v.owner_user_id,
              due_date: v.due_date ? dayjs(v.due_date).format('YYYY-MM-DD') : undefined,
              create_follow_up: true,
            })
          }
        >
          <Form.Item name="decision_text" label="Decision / instruction" rules={[{ required: true }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="owner_user_id" label="Owner">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={users.map((u: any) => ({
                value: u.id,
                label: crmPersonName(u) || u.email,
              }))}
            />
          </Form.Item>
          <Form.Item name="due_date" label="Due date (creates CRM follow-up)">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default StandupTeamPage;
